/**
 * Reconcile paint-first album wall rows against authoritative get_albums results.
 * Handles ghost clicks (get_album no rows) without error-boundary throws.
 */

import { showImperativeToast } from '../providers/UIProvider';
import { normalizeLocalAlbumId } from './localLibraryMetadata';
import { purgeStaleLocalAlbumRef } from './staleLocalAlbumPurge';
import { removeAlbumFromLocalIndex } from './libraryAlbumIndex';

export function albumTileId(row: Record<string, unknown> | null | undefined): string {
  return String(row?.album_key ?? row?.id ?? '').trim();
}

/**
 * When `authoritative` is true, fresh get_albums is the source of truth:
 * drop persisted tiles whose ids are absent from fresh; preserve field merges for matches.
 */
export function reconcilePersistedAgainstFreshCatalog(
  persisted: Record<string, unknown>[],
  fresh: Record<string, unknown>[],
  options: { authoritative?: boolean } = {},
): { albums: Record<string, unknown>[]; removedIds: string[] } {
  if (!options.authoritative) {
    return { albums: persisted ?? [], removedIds: [] };
  }

  const prev = Array.isArray(persisted) ? persisted : [];
  const nextFresh = Array.isArray(fresh) ? fresh : [];

  if (!nextFresh.length) {
    const removedIds = prev.map(albumTileId).filter(Boolean);
    return { albums: [], removedIds };
  }

  const persistedByKey = new Map<string, Record<string, unknown>>();
  for (const row of prev) {
    const k = albumTileId(row);
    if (k) persistedByKey.set(k, row);
  }

  const freshIdSet = new Set<string>();
  const albums = nextFresh.map((row) => {
    const k = albumTileId(row);
    if (k) freshIdSet.add(k);
    const prior = k ? persistedByKey.get(k) : null;
    return prior ? { ...prior, ...row } : row;
  });

  const removedIds: string[] = [];
  for (const row of prev) {
    const k = albumTileId(row);
    if (k && !freshIdSet.has(k)) removedIds.push(k);
  }

  return { albums, removedIds };
}

const GHOST_TOAST_COOLDOWN_MS = 3000;
let lastGhostToastAt = 0;

/** Info toast + tile removal + one background get_albums refresh (no wall remount). */
export function handleGhostAlbumOpen(albumKey: string): void {
  const normalized = normalizeLocalAlbumId(String(albumKey || '').trim());
  if (!normalized) return;

  removeAlbumFromLocalIndex(normalized);
  purgeStaleLocalAlbumRef(normalized);

  const now = Date.now();
  if (now - lastGhostToastAt >= GHOST_TOAST_COOLDOWN_MS) {
    lastGhostToastAt = now;
    showImperativeToast(
      'That album was reorganized during a library update — refreshing your library view',
      5000,
      { severity: 'info' },
    );
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { kind: 'album', id: normalized, reason: 'stale-tile-removed' },
    }));
    window.dispatchEvent(new CustomEvent('maestro:library:changed', {
      detail: { reason: 'stale-tile-reconcile', backgroundOnly: true },
    }));
  }
}
